import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import html2canvas from "html2canvas";
import { ArrowUpCircle, Plus } from "lucide-react";
import { toast } from "react-toastify";
import {
  deleteRestaurantMenu,
  getPublicMenuPath,
  getRestaurantMenus,
  setRestaurantMenuStatus,
} from "../api/menu";
import MenuCategoryOverview from "../components/menu-management/MenuCategoryOverview";
import MenuLifecycleActions from "../components/menu-management/MenuLifecycleActions";
import MenuListGrid from "../components/menu-management/MenuListGrid";
import MenuOverviewEmptyState from "../components/menu-management/MenuOverviewEmptyState";
import MenuOverviewSkeleton from "../components/menu-management/MenuOverviewSkeleton";
import MenuOverviewSummary from "../components/menu-management/MenuOverviewSummary";
import MenuShareCard from "../components/menu-management/MenuShareCard";
import API_URL from "../config/apiConfig";
import { FoodServiceContext } from "../context/FoodServiceContext";
import { useEntitlements } from "../context/EntitlementsContext";
import { PLAN_LIMIT } from "../config/planEntitlements";
import {
  applyMenuStatusChange,
  buildMenuOverview,
  getMenuOverviewPath,
  getMenuStatusMeta,
} from "../services/menuOverviewModel";
import { errorMessage } from "../utils/errorMessage";

export default function MenuPanel() {
  const qrCardRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { restaurantData, restaurantSettings } = useContext(FoodServiceContext);
  const { entitlements } = useEntitlements();
  const maxMenus = entitlements?.limits?.[PLAN_LIMIT.MAX_MENUS] ?? null;
  const preferredMenuId = new URLSearchParams(location.search).get("menuId");
  const [menus, setMenus] = useState([]);
  const [status, setStatus] = useState("loading");
  const [loadError, setLoadError] = useState(null);
  const [busyAction, setBusyAction] = useState(null);

  const loadMenus = useCallback(async () => {
    setStatus("loading");
    setLoadError(null);
    try {
      const result = await getRestaurantMenus();
      setMenus(result);
      setStatus("ready");
    } catch (error) {
      setLoadError(errorMessage(error, "دریافت اطلاعات منو ناموفق بود."));
      setStatus("error");
    }
  }, []);

  useEffect(() => {
    loadMenus();
  }, [loadMenus]);

  const overview = useMemo(
    () => buildMenuOverview({ menus, preferredMenuId }),
    [menus, preferredMenuId]
  );
  const statusMeta = overview.status ? getMenuStatusMeta(overview.status) : null;
  const publicMenuPath = getPublicMenuPath(restaurantData?.menuLink);
  const publicMenuUrl = publicMenuPath ? `${window.location.origin}${publicMenuPath}` : null;
  const selectedPublicMenuPath = overview.isPublished ? publicMenuPath : null;
  const selectedPublicMenuUrl = overview.isPublished ? publicMenuUrl : null;
  const restaurantId = restaurantData?._id;
  const logoUrl = restaurantSettings?.profileImage
    ? `${API_URL}/uploads/restaurants/${restaurantId}/${restaurantSettings.profileImage}`
    : "/images/mizban.png";

  const selectMenu = (menuId) => {
    navigate(getMenuOverviewPath(menuId));
  };

  const atMenuLimit = maxMenus != null && menus.length >= maxMenus;

  const changeStatus = async (nextStatus) => {
    if (!overview.menuId) return;
    if (nextStatus === "disabled" && !window.confirm("منو از دسترس مشتریان خارج شود؟")) return;

    const action = nextStatus === "published" ? "publish" : "disable";
    setBusyAction(action);
    try {
      const result = await setRestaurantMenuStatus(overview.menuId, nextStatus);
      setMenus((current) => applyMenuStatusChange(current, result.menu));
      toast.success(nextStatus === "published" ? "منو منتشر شد" : "منو غیرفعال شد");
    } catch (error) {
      toast.error(errorMessage(error, "تغییر وضعیت منو ناموفق بود."));
    } finally {
      setBusyAction(null);
    }
  };

  const deleteMenu = async () => {
    if (!overview.menuId || !window.confirm("این منو برای همیشه حذف شود؟ این کار قابل بازگشت نیست.")) return;

    setBusyAction("delete");
    try {
      await deleteRestaurantMenu(overview.menuId);
      setMenus((current) => current.filter((menu) => String(menu._id) !== String(overview.menuId)));
      navigate(getMenuOverviewPath(), { replace: true });
      toast.success("منو حذف شد");
    } catch (error) {
      toast.error(errorMessage(error, "حذف منو ناموفق بود."));
    } finally {
      setBusyAction(null);
    }
  };

  const copyPublicLink = async () => {
    if (!selectedPublicMenuUrl || !navigator.clipboard?.writeText) {
      toast.error("امکان کپی لینک در این مرورگر در دسترس نیست.");
      return;
    }

    try {
      await navigator.clipboard.writeText(selectedPublicMenuUrl);
      toast.success("لینک منو کپی شد");
    } catch {
      toast.error("کپی لینک ناموفق بود.");
    }
  };

  const sharePublicLink = async () => {
    if (!selectedPublicMenuUrl) return;
    if (!navigator.share) {
      await copyPublicLink();
      return;
    }

    try {
      await navigator.share({ title: overview.title, url: selectedPublicMenuUrl });
    } catch (error) {
      if (error?.name !== "AbortError") toast.error("اشتراک‌گذاری لینک ناموفق بود.");
    }
  };

  const downloadQr = async () => {
    if (!qrCardRef.current || !selectedPublicMenuUrl) return;
    try {
      const canvas = await html2canvas(qrCardRef.current, {
        useCORS: true,
        backgroundColor: "#fff",
        scale: 3,
      });
      const link = document.createElement("a");
      link.href = canvas.toDataURL("image/png");
      link.download = "mizban-menu-qr.png";
      link.click();
    } catch {
      toast.error("دانلود QR ناموفق بود.");
    }
  };

  if (status === "loading") return <MenuOverviewSkeleton />;

  if (status === "error") {
    return (
      <div className="mx-auto w-full max-w-7xl p-4 sm:p-6 lg:p-8">
        <section className="rounded-2xl border border-red-200 bg-white p-6 text-center" role="alert">
          <h1 className="font-extrabold text-accent">اطلاعات منو دریافت نشد</h1>
          <p className="mt-2 text-sm text-red-700">{loadError}</p>
          <button type="button" onClick={loadMenus} className="mt-4 min-h-10 rounded-xl bg-primary px-5 text-sm font-bold text-white focus:outline-none focus:ring-4 focus:ring-primary/20">تلاش دوباره</button>
        </section>
      </div>
    );
  }

  return (
    <main className="mx-auto w-full max-w-6xl p-4 sm:p-5 lg:p-6" dir="rtl">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-black text-accent">مدیریت منو</h1>
          <p className="mt-1 text-xs leading-5 text-gray-500">محتوا، وضعیت انتشار و لینک منوی آنلاین رستوران را از اینجا مدیریت کنید.</p>
        </div>
        {overview.hasMenu && (
          <Link
            to={atMenuLimit ? "/dashboard/pricing" : "/dashboard/edit-menu"}
            title={atMenuLimit ? `بسته فعلی شما امکان ساخت حداکثر ${maxMenus} منو را می‌دهد.` : undefined}
            className="inline-flex h-10 shrink-0 items-center justify-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3.5 text-sm font-bold text-accent transition-colors hover:bg-gray-50 focus:outline-none focus:ring-4 focus:ring-gray-100"
          >
            {atMenuLimit ? <ArrowUpCircle size={15} aria-hidden="true" /> : <Plus size={15} aria-hidden="true" />}
            {atMenuLimit ? "ارتقا برای منوی بیشتر" : "منوی جدید"}
          </Link>
        )}
      </header>

      {!overview.hasMenu ? (
        <MenuOverviewEmptyState />
      ) : (
        <div className="mt-4 space-y-4">
          <MenuListGrid menus={menus} activeMenuId={overview.menuId} onSelect={selectMenu} />

          <div className="grid grid-cols-[minmax(0,1fr)] items-start gap-4 lg:grid-cols-[minmax(0,1fr)_18rem]">
            <div className="min-w-0 space-y-4">
              <MenuOverviewSummary
                overview={overview}
                statusTone={statusMeta?.tone}
                publicMenuPath={selectedPublicMenuPath}
                actions={(
                  <MenuLifecycleActions
                    isPublished={overview.isPublished}
                    busyAction={busyAction}
                    onPublish={() => changeStatus("published")}
                    onDisable={() => changeStatus("disabled")}
                    onDelete={deleteMenu}
                  />
                )}
              />
              <MenuCategoryOverview menu={overview.menu} editPath={`/dashboard/edit-menu/${overview.menuId}`} />
            </div>
            <aside>
              <MenuShareCard
                ref={qrCardRef}
                publicMenuUrl={selectedPublicMenuUrl}
                logoUrl={logoUrl}
                unavailableMessage={overview.isPublished ? undefined : "برای ساخت لینک و QR این منو، ابتدا آن را منتشر کنید."}
                onCopy={copyPublicLink}
                onShare={sharePublicLink}
                onDownload={downloadQr}
              />
            </aside>
          </div>
        </div>
      )}
    </main>
  );
}
